<template>
  <a-spin
    :spinning="isLoading"
    class="custom-spinner"
    style="height: 100vh; top: 20%"
  >
    <a-row class="performer-project-tasks-label" v-if="hasSlot">
      <slot name="header"></slot>
    </a-row>
    <task-item
      v-for="(task, index) in tasks"
      :key="index++"
      :task="{ number: index++, ...task }"
      :show-availability="isAvailabilityVisible"
      :disabled="
        disabled ||
        [PROJECT_STATUS_ACCEPTANCE, PROJECT_STATUS_CLOSED].includes(
          projectStatusId
        )
      "
      :editor-user-id="editorUserId"
      @update="onUpdate"
    />

    <c-button-secondary
      v-if="currentPage < totalPages"
      class="page-load-button"
      @click="handleNextPage"
    >
      Показать ещё
    </c-button-secondary>
  </a-spin>
</template>

<script setup>
  import TaskItem from '@/components/projects/management/Task/TaskItem.vue';
  import {
    PROJECT_STATUS_ACCEPTANCE,
    PROJECT_STATUS_CLOSED,
  } from '@/api/models/ProjectStatus';
  import CButtonSecondary from '@/components/UI/CButtonSecondary.vue';
  import { useApi } from '@/api/use-api';
  import { useRoute } from 'vue-router';
  import { computed, ref, useSlots, watch } from 'vue';
  import {
    USER_ROLE_LEADER,
    USER_ROLE_PARTNER,
    USER_ROLE_PERFORMER,
  } from '@/api/models/UserRole';
  import {
    STATUS_COMPLETED,
    STATUS_COMPLETED_OVERDUE,
    STATUS_INCOMPLETE,
    STATUS_INCOMPLETE_OVERDUE,
  } from '@/api/models/Checkpoint';

  const api = useApi();
  const route = useRoute();
  const emit = defineEmits(['update']);

  const props = defineProps({
    taskStatus: {
      type: String,
      required: true,
    },
    projectStatusId: {
      type: Number,
    },
    userId: {
      type: Number,
    },
    showAvailability: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    partnerMode: {
      type: Boolean,
      default: false,
    },
  });
  const isLoading = ref(false);
  const tasks = ref([]);

  const PAGE_SIZE = 20;
  const totalPages = ref(0);
  const lastPage = ref(0);
  const currentPage = ref(1);

  const slots = useSlots();
  const hasSlot = computed(() => {
    return !!slots['header'];
  });

  const isAvailabilityVisible = computed(() => {
    return props.showAvailability && route.meta.role === USER_ROLE_LEADER;
  });

  const tab = computed(() => {
    return route.hash
      ? route.hash.replace('#', '')
      : route.meta.role === USER_ROLE_PERFORMER
      ? 'my'
      : '';
  });

  const editorUserId = computed(() => {
    return route.meta.role === USER_ROLE_PERFORMER ? props.userId : null;
  });

  watch(
    () => props.projectStatusId,
    async () => {
      await getTasks();
    }
  );

  watch(
    () => route.hash,
    async () => {
      await updateTasks();
    }
  );

  const getTasks = async () => {
    if (tab.value === 'my' && !props.userId) return;

    isLoading.value = true;

    const searchFilter = new URLSearchParams({
      projectId: route.params.id,
      status: props.taskStatus,
      'per-page': PAGE_SIZE,
      page: currentPage.value,
    });

    if (route.meta.role === USER_ROLE_PARTNER) {
      searchFilter.append('visibility', '1');
    } else if ((!tab.value && props.userId) || tab.value === 'my') {
      searchFilter.append('userId', props.userId);
    }

    try {
      let response;

      if (USER_ROLE_LEADER === route.meta.role) {
        response = await api.projectManagementPage.tasks(searchFilter);
      } else {
        response = await api.performerPage.tasks(searchFilter);
      }

      totalPages.value = response.tasks.pages.total;

      if (lastPage.value !== currentPage.value) {
        tasks.value = tasks.value.concat(updateTaskStatus(response.tasks.items));
        lastPage.value = currentPage.value;
      }
    } catch (error) {
      console.error('error', error);
    } finally {
      isLoading.value = false;
    }
  };

  const onUpdate = async () => {
    emit('update');
  };

  const updateTasks = async () => {
    currentPage.value = 1;
    lastPage.value = 0;
    totalPages.value = 0;
    tasks.value = [];
    await getTasks();
  };

  const handleNextPage = async () => {
    if (currentPage.value < totalPages.value) {
      currentPage.value++;
      await getTasks();
    }
  };

  defineExpose({ updateTasks });

  // TODO: перенести логику вычисления разных статусов контрольных точек 
  // для разных ролей на бэк-энд
  const updateTaskStatus = (tasks) => {
    if (!props.partnerMode) return tasks;

    return tasks.map((task) => {
      task.checkpoints = task.checkpoints.map((checkpoint) => {
        if (checkpoint.status === STATUS_INCOMPLETE_OVERDUE)
          checkpoint.status = STATUS_INCOMPLETE;
        if (checkpoint.status === STATUS_COMPLETED_OVERDUE)
          checkpoint.status = STATUS_COMPLETED;
        return checkpoint;
      });
      task.subtasks = updateTaskStatus(task.subtasks);
      return task;
    });
  };
</script>

<style lang="scss" scoped>
  @use "@/assets/styles/_colors.scss" as *;

  .performer-project-tasks-label {
    align-items: center;
    background-color: $color-bg-primary-black-100;
    color: $color-bg-primary-white;
    display: flex;
    height: 32px;
    justify-content: center;
    margin: 40px 0 20px 0;
    text-align: center;
  }

  .page-load-button {
    width: 100%;
  }
</style>
